import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import starMap from '../../constants/starMap';

import Star from '../Star';

import './Rating.scss';
import { getTranslation } from '../../providers/TranslationProvider';

const renderTotal = (total) => {
  if (!total) {
    return null;
  }

  if (typeof total === 'string') {
    total = parseInt(total, 10) || null;
  }

  const locale = navigator.language || 'en-US';
  const localizedResult = new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(total);

  const reviewCount = (localizedResult === '1')
    ? getTranslation({
      translationKey: 'review_label',
      fallback: '(1) review',
    })
    : getTranslation({
      translationKey: 'reviews_label',
      translationValues: { localizedResult },
      fallback: `(${localizedResult}) reviews`,
    });

  return (
    <span className="ratings">{reviewCount}</span>
  );
};

const buildRatingMap = ({ score, scale }) => {
  const { EMPTY, HALF, FULL } = starMap;
  const ratingToNearestHalf = Math.round(score * 2) / 2;
  const fullStarCount = Math.floor(ratingToNearestHalf);
  const hasHalfStar = fullStarCount !== ratingToNearestHalf;
  let emptyStarCount = scale - fullStarCount;
  let halfStarCount = 0;

  if (hasHalfStar) {
    halfStarCount = 1;
    emptyStarCount -= 1;
  }

  return {
    [FULL]: fullStarCount,
    [HALF]: halfStarCount,
    [EMPTY]: emptyStarCount,
  };
};

const renderStarRating = (ratingMap) => {
  let count = 1;
  const starRating = Object.keys(ratingMap).reduce((acc, key) => {
    const temp = [];
    for (let i = 0; i < ratingMap[key]; i += 1) {
      temp.push(
        <Star
          className="Star--gray"
          type={key}
          key={count}
        />
      );
      count += 1;
    }
    return [...acc, ...temp];
  }, []);

  return starRating;
};

const Rating = ({
  attribution,
  scale,
  score,
  thirdParty,
  totalRatings,
  soldout,
}) => {
  const ariaLabel = getTranslation({
    translationKey: 'rating_label',
    translationValues: {
      score: score?.toFixed(1),
      scale,
    },
    fallback: `${score && score.toFixed(1)} out of ${scale} stars`,
  });

  if (thirdParty) {
    return (
      <div className="Rating">
        <div className="Rating__star-wrapper" role="img" aria-label={ariaLabel}>
          {renderStarRating(buildRatingMap({ score, scale }))}
        </div>
        <div className="Rating__attribution">
          {attribution}
        </div>
      </div>
    );
  }

  return (
    <div className="Rating">
      <div
        className={cx('Rating', {
          soldoutrate: (soldout),
        })}
        role="img"
        aria-label={ariaLabel}
      >
        <Star />
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <span className="score">{score.toFixed(1)}</span>
      </div>
      {renderTotal(totalRatings)}
      {attribution && (
        <div className="Rating__attribution">
          &mdash;&nbsp;
          {attribution}
        </div>
      )}
    </div>
  );
};

Rating.propTypes = {
  attribution: PropTypes.string,
  scale: PropTypes.number,
  score: PropTypes.number,
  thirdParty: PropTypes.bool,
  totalRatings: PropTypes.number,
  soldout: PropTypes.bool,
};

Rating.defaultProps = {
  attribution: undefined,
  scale: 5,
  score: 5,
  thirdParty: false,
  totalRatings: undefined,
  soldout: false,
};

Rating.displayName = 'Rating';
export default Rating;
